import { Controller } from "@hotwired/stimulus";

// Keeps a board current without a page reload.
//
// WATERMARK FIRST, REPLACE SECOND. Polling the panel itself would re-run the whole rollup and ship
// the whole fragment every tick, most of which return the same numbers — a site sells a few
// times an hour, not a few times a second. So each tick asks a single indexed question ("what is the
// newest updated_at"), and only a moved answer costs a frame replace.
//
// The frame reload is what redraws the charts: chart_controller builds its chart in connect(), so a
// replaced frame re-mounts and rebuilds with no update path of its own to maintain.
const POLL_MS = 3000;
// The badge's color is the HEALTH OF THE PIPE, never the age of the data: green while polls answer
// and the store answers, yellow when our own polls stop coming back (we cannot see, so the figures
// on screen are stale by an unknown amount), red when Rails answers and reports the store refused.
// "Last sale 40m ago" stays green on a quiet morning -- that is a fact about the site, and
// coloring it would train people to ignore the one signal that matters when the pipe really breaks.
const BADGE = {
  ok:           { text: "Live",             classes: ["border-green-200", "bg-green-50", "text-green-700"] },
  disconnected: { text: "Connection Lost",  classes: ["border-amber-200", "bg-amber-50", "text-amber-800"] },
  down:         { text: "Store Unreachable", classes: ["border-red-200", "bg-red-50", "text-red-700"] },
};

export default class extends Controller {
  static targets = ["frame", "status"];
  static values = { watermarkUrl: String, panelUrl: String };

  connect() {
    this.lastSeen = this.currentWatermark();
    this.health = "ok"; // rendered server-side a moment ago, so the pipe was just proven
    this.lastRevision = null; // first tick seeds it; a null here cannot mask a change
    this.locationId = new URL(window.location).searchParams.get("location_id") || "";
    this.renderStatus();
    this.timer = setInterval(() => this.tick(), POLL_MS);
  }

  disconnect() {
    clearInterval(this.timer);
  }

  currentWatermark() {
    const raw = this.hasFrameTarget ? this.frameTarget.dataset.watermark : "";
    return raw ? Number(raw) : null;
  }

  async tick() {
    // A BACKGROUNDED TAB IS USUALLY MOST OF THE REQUEST VOLUME. Nobody is reading a hidden board, and
    // it costs a query per tick per tab forever. Skipping is enough on its own -- connect() reads the
    // watermark rendered into the frame, and the first visible tick catches up whatever it missed.
    if (document.visibilityState === "hidden") return;

    // A failed poll is not worth surfacing: the next tick is five seconds away and the numbers on
    // screen are still the last true ones. Leave the status line alone and try again.
    try {
      const response = await fetch(this.scopedUrl(this.watermarkUrlValue), {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) {
        this.health = "disconnected";
        this.renderStatus();
        return;
      }

      const { watermark, revision, store } = await response.json();
      const wasBroken = this.health !== "ok";
      this.health = store === "down" ? "down" : "ok";
      // RECOVERY MUST REFRESH EVEN WITH NO SALES. If the page rendered the unavailable banner during an
      // outage and the store came back before the day's first sale, watermark is null and nothing below
      // would fire -- the banner would sit there until a sale happened to land.
      //
      // `revision` is the row count, so a late arrival carrying an OLDER occurred_at still triggers a
      // refresh where the timestamp alone would not have moved.
      const changed =
        (watermark && watermark !== this.lastSeen) || (revision != null && revision !== this.lastRevision);
      if (this.health === "ok" && (changed || wasBroken)) {
        this.lastSeen = watermark;
        this.lastRevision = revision;
        await this.replacePanel();
      }
      this.renderStatus();
    } catch {
      // Offline or mid-deploy: the poll itself failed, which is exactly what yellow means. Keep the
      // last known figures rather than blanking the board.
      this.health = "disconnected";
      this.renderStatus();
    }
  }

  // A location change is a data change, not a navigation: the panel morphs in place and the URL is
  // rewritten so the page stays shareable. Navigating instead would reload the world -- the biggest
  // flash there is, on a page whose whole contract is that nothing flashes.
  async locationChanged(event) {
    event.preventDefault?.();
    this.locationId = event.target.value ?? "";
    const url = new URL(window.location);
    if (this.locationId) url.searchParams.set("location_id", this.locationId);
    else url.searchParams.delete("location_id");
    history.replaceState(null, "", url);
    this.lastSeen = null; // the new scope has its own watermark; the swap below re-seeds it
    await this.replacePanel();
    this.renderStatus();
  }

  scopedUrl(base) {
    const url = new URL(base, window.location.origin);
    if (this.locationId) url.searchParams.set("location_id", this.locationId);
    else url.searchParams.delete("location_id");
    return url.toString();
  }

  // MORPH, DON'T REPLACE. The panel's skeleton is constant by construction (zeros render, rows never
  // appear or vanish), so an update is: swap the text of any [data-sync] node whose value moved, and
  // rewrite each chart's data attribute -- Stimulus sees the mutation, fires panelsValueChanged, and
  // Chart.js TWEENS to the new data instead of rebuilding from scratch. Unchanged nodes are never
  // touched, so nothing repaints on a tick where nothing happened. The outerHTML path survives only
  // as the fallback for a genuine skeleton change (the unavailable banner coming or going), where a
  // hard swap is honest.
  //
  // Turbo frame reload() was tried and failed twice before this (no [src] = silent no-op; [src] =
  // eager refetch that blanked the panel), which is why this depends on nothing but fetch and DOM.
  async replacePanel() {
    const response = await fetch(this.scopedUrl(this.panelUrlValue), { headers: { Accept: "text/html" } });
    if (!response.ok) return;

    const html = await response.text();
    if (!html.includes("realtime_sales_panel")) return; // an error page must not eat the board

    const incoming = document.createRange().createContextualFragment(html)
      .querySelector("[data-realtime-poll-target='frame']");
    if (!incoming) return;

    const frame = this.frameTarget;
    const oldCharts = frame.querySelectorAll("[data-controller~='chart']");
    const newCharts = incoming.querySelectorAll("[data-controller~='chart']");
    const oldRegions = frame.querySelectorAll("[data-morph-region]");
    const newRegions = incoming.querySelectorAll("[data-morph-region]");
    // Values OUTSIDE any region. Their key list is fixed by construction, so it is a true skeleton
    // check; a region's rows are excluded precisely because they are allowed to churn.
    const outside = (root) =>
      Array.from(root.querySelectorAll("[data-sync]")).filter((n) => !n.closest("[data-morph-region]"));
    const oldSync = outside(frame);
    const newSync = outside(incoming);

    // KEYS, NOT JUST COUNTS -- but only for the fixed part of the page. Comparing the key list of EVERY
    // [data-sync] node was the mistake: Top Items keys carry their sellable_id, so one item overtaking
    // another changed the list and dropped the whole panel into the hard swap below. That re-mounts
    // chart_controller, and every bar chart animated up from zero on an ordinary sale -- the exact
    // jarring redraw this morph exists to avoid, and worse than the mispairing it was fixing.
    const keysOf = (nodes) => Array.from(nodes, (n) => n.dataset.sync).join("|");
    const sameSkeleton =
      oldSync.length > 0 &&
      oldCharts.length === newCharts.length &&
      oldRegions.length === newRegions.length &&
      keysOf(oldSync) === keysOf(newSync);
    if (!sameSkeleton) {
      // A GENUINE structural change: the unavailable banner arriving or leaving. A hard swap is honest
      // there, because the page really is a different page.
      frame.outerHTML = incoming.outerHTML;
      this.lastSeen = this.currentWatermark();
      return;
    }

    this.syncValues(oldSync, newSync);

    // Each region in place: same rows, same order → update the figures and pulse them like any other
    // value. Different rows or a different order → re-render just this list. Either way the charts are
    // untouched, so they tween from where they are.
    newRegions.forEach((node, i) => {
      const old = oldRegions[i];
      const oldRows = old.querySelectorAll("[data-sync]");
      const newRows = node.querySelectorAll("[data-sync]");
      if (keysOf(oldRows) === keysOf(newRows)) this.syncValues(oldRows, newRows);
      else if (old.innerHTML !== node.innerHTML) old.innerHTML = node.innerHTML;
    });

    newCharts.forEach((node, i) => {
      // panels-value must sync LAST: the panelsValueChanged redraw needs the other attributes in place.
      const old = oldCharts[i];
      const names = node.getAttributeNames()
        .filter((n) => n.startsWith("data-chart-"))
        .sort((a, b) => (a === "data-chart-panels-value") - (b === "data-chart-panels-value"));
      for (const name of names) {
        const v = node.getAttribute(name);
        if (v !== null && old.getAttribute(name) !== v) old.setAttribute(name, v);
      }
    });

    frame.dataset.watermark = incoming.dataset.watermark || "";
    this.lastSeen = this.currentWatermark();
  }

  // Paired by position within an already key-matched list, so index IS key here. Unchanged nodes are
  // never touched: nothing repaints on a tick where nothing happened, which is rule one of a TV page.
  syncValues(oldNodes, newNodes) {
    newNodes.forEach((node, i) => {
      const old = oldNodes[i];
      if (!old) return;
      if (old.textContent !== node.textContent) {
        old.textContent = node.textContent;
        // One soft pulse on the value that moved -- enough to catch an eye on a TV, not a flash.
        // Optional because the pulse is decoration and the VALUES are the point: somewhere without the
        // Web Animations API should get an un-pulsed correct board, not a morph that throws half way
        // through and leaves some figures updated and others stale.
        old.animate?.([{ opacity: 0.35 }, { opacity: 1 }], { duration: 400, easing: "ease-out" });
      }
      // Item names are truncated with the full name in a title tooltip, so the tooltip is part of the
      // value and goes stale with it. Only ever carried across, never invented.
      if (node.title && old.title !== node.title) old.title = node.title;
    });
  }

  // The badge names the last SALE, not the last update -- the panel updates itself in real time, so
  // "last updated" was always seconds and said nothing. The watermark IS max(occurred_at), so the age
  // of the newest sale is exactly what it measures. lastSeen is epoch MILLIS: multiplying by 1000
  // here once made every age negative and the badge lied "live" through a two-hour freeze.
  renderStatus() {
    if (!this.hasStatusTarget) return;
    const badge = this.statusTarget;
    const state = BADGE[this.health] ?? BADGE.ok;

    const age = this.lastSeen ? ` · Last Sale ${this.ago(Date.now() - this.lastSeen)} Ago` : "";
    if (!this.lastSeen && this.health === "ok") {
      badge.hidden = true; // nothing to date and nothing wrong: the empty state already says it
      return;
    }
    badge.hidden = false;
    // The dot as markup, not a text ●: only an element can pulse. bg-current inherits each state's
    // text color, and the ping runs only while the pipe is provably live.
    const dot = this.health === "ok"
      ? '<span class="relative inline-flex h-2 w-2"><span class="absolute inline-flex h-full w-full rounded-full bg-current opacity-75 motion-safe:animate-ping"></span><span class="relative inline-flex h-2 w-2 rounded-full bg-current"></span></span>'
      : '<span class="inline-flex h-2 w-2 rounded-full bg-current"></span>';
    badge.innerHTML = `${dot}<span>${state.text}${age}</span>`;
    for (const s of Object.values(BADGE)) {
      for (const c of s.classes) badge.classList.remove(c);
    }
    badge.classList.add(...state.classes);
  }

  ago(ms) {
    const seconds = Math.max(0, Math.round(ms / 1000));
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
    return `${Math.floor(seconds / 86400)}d`;
  }
}
